import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import DashboardLayout from "../../components/DashboardLayout/DashboardLayout";
import ExpandableText from "../../components/ExpandableText";
import { ImpactTileSkeleton } from "../../components/Skeleton";
import { apiUrl } from "../../config/api";
import { formatDate } from "../../i18n/format";
import {
  getArticlesListQueryKey,
  useArticlesList,
  useArticlesWatchlistCreate,
  useArticlesWatchlistDestroy,
} from "../../services/articles/generated/articles/articles";
import "./MarketIntelligencePage.css";

type MarketTab = "all" | "regulations" | "geopolitics" | "business" | "watchlist";

function formatReleaseDate(iso: string): string {
  return formatDate(iso, { year: "numeric", month: "long", day: "numeric" });
}

const CATEGORY_LABEL_KEYS = {
  REGULATION: "marketIntelligence.category.regulation",
  GEOPOLITICS: "marketIntelligence.category.geopolitics",
  BUSINESS_NEWS: "marketIntelligence.category.businessNews",
} as const;

function categoryLabelKey(category: string) {
  return category in CATEGORY_LABEL_KEYS ? CATEGORY_LABEL_KEYS[category as keyof typeof CATEGORY_LABEL_KEYS] : null;
}

function categoryLabel(category: string): string {
  return category.replace(/_/g, " ");
}

function categoryBadgeClass(category: string): string {
  return category.toLowerCase().replace(/_/g, "-");
}

const MarketIntelligencePage = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation("b2b");
  const [activeTab, setActiveTab] = useState<MarketTab>("all");

  // Two queries: the full feed (for counters + tabs) and a watchlist-only feed
  // when the user is on the Watchlist tab. Keeping them split keeps invalidation
  // simple — bookmarking flips both queries' cache entries.
  const fullFeedParams = undefined;
  const watchlistParams = { watchlistedOnly: true };

  const { data: articlesData, isLoading: loading, error: queryError } = useArticlesList(fullFeedParams);
  const { data: watchlistData } = useArticlesList(watchlistParams, {
    query: { enabled: activeTab === "watchlist" },
  });

  const articles = articlesData?.results || [];
  const watchlistArticles = watchlistData?.results || [];

  const error = queryError ? t("marketIntelligence.errors.load") : null;

  const invalidateFeeds = () => {
    queryClient.invalidateQueries({ queryKey: getArticlesListQueryKey(fullFeedParams) });
    queryClient.invalidateQueries({ queryKey: getArticlesListQueryKey(watchlistParams) });
  };

  const addToWatchlist = useArticlesWatchlistCreate({
    mutation: { onSuccess: invalidateFeeds },
  });
  const removeFromWatchlist = useArticlesWatchlistDestroy({
    mutation: { onSuccess: invalidateFeeds },
  });

  const toggleWatchlist = (id: number, isWatchlisted: boolean) => {
    if (isWatchlisted) {
      removeFromWatchlist.mutate({ id });
    } else {
      addToWatchlist.mutate({ id });
    }
  };

  const visibleArticles = (() => {
    if (activeTab === "watchlist") return watchlistArticles;
    if (activeTab === "all") return articles;
    if (activeTab === "regulations") return articles.filter((a) => a.category === "REGULATION");
    if (activeTab === "geopolitics") return articles.filter((a) => a.category === "GEOPOLITICS");
    return articles.filter((a) => a.category === "BUSINESS_NEWS");
  })();

  const regulationsCount = articles.filter((a) => a.category === "REGULATION").length;
  const geopoliticsCount = articles.filter((a) => a.category === "GEOPOLITICS").length;
  const businessNewsCount = articles.filter((a) => a.category === "BUSINESS_NEWS").length;
  const watchlistedCount = articles.filter((a) => a.isWatchlisted).length;

  const latestArticle = articles[0];

  return (
    <DashboardLayout showSearch={false}>
      <div className="market-intel">
        <header className="dashboard__section-header">
          <div>
            <h1 className="dashboard__title">{t("marketIntelligence.title")}</h1>
            <p className="dashboard__subtitle">{t("marketIntelligence.subtitle")}</p>
          </div>
          <button
            type="button"
            className="market-intel__export-button"
            onClick={() => {
              window.location.href = apiUrl("/api/v1/articles/recent/exportPdf/");
            }}
            disabled={articles.length === 0}
          >
            <i className="fa-regular fa-file-pdf" />
            <span>{t("marketIntelligence.exportPdf")}</span>
          </button>
        </header>

        <section className="market-intel__impact-card" aria-label={t("marketIntelligence.impact.ariaLabel")}>
          <header className="market-intel__impact-header">
            <div className="market-intel__impact-icon">
              <i className="fa-solid fa-dollar-sign" />
            </div>
            <h2 className="market-intel__impact-title">{t("marketIntelligence.impact.title")}</h2>
          </header>

          {loading ? (
            <div className="market-intel__impact-grid">
              <ImpactTileSkeleton count={3} />
            </div>
          ) : (
            <>
              <div className="market-intel__impact-grid">
                <article className="market-intel__impact-tile market-intel__impact-tile--purple">
                  <p className="market-intel__impact-tile-label">{t("marketIntelligence.impact.totalArticles")}</p>
                  <p className="market-intel__impact-tile-value">{articles.length}</p>
                  <p className="market-intel__impact-tile-meta">{t("marketIntelligence.impact.inYourFeed")}</p>
                </article>
                <article className="market-intel__impact-tile market-intel__impact-tile--yellow">
                  <p className="market-intel__impact-tile-label">{t("marketIntelligence.impact.regulatoryChanges")}</p>
                  <p className="market-intel__impact-tile-value">{regulationsCount}</p>
                  <p className="market-intel__impact-tile-meta">{t("marketIntelligence.impact.affectingYourSector")}</p>
                </article>
                <article className="market-intel__impact-tile market-intel__impact-tile--green">
                  <p className="market-intel__impact-tile-label">
                    {t("marketIntelligence.impact.businessGeopolitics")}
                  </p>
                  <p className="market-intel__impact-tile-value">{businessNewsCount + geopoliticsCount}</p>
                  <p className="market-intel__impact-tile-meta">{t("marketIntelligence.impact.inTargetMarkets")}</p>
                </article>
              </div>

              {latestArticle ? (
                <div className="market-intel__impact-insight">
                  <h3 className="market-intel__impact-insight-title">
                    {t("marketIntelligence.impact.latest", { title: latestArticle.title })}
                  </h3>
                  <ExpandableText
                    className="market-intel__impact-insight-text"
                    text={latestArticle.brief || t("marketIntelligence.impact.openArticleHint")}
                    maxLines={3}
                  />
                </div>
              ) : null}
            </>
          )}
        </section>

        <section className="market-intel__tabs-section">
          <div className="market-intel__tabs">
            <button
              type="button"
              className={`market-intel__tab ${activeTab === "all" ? "market-intel__tab--active" : ""}`}
              onClick={() => setActiveTab("all")}
            >
              {t("marketIntelligence.tabs.all")}
            </button>
            <button
              type="button"
              className={`market-intel__tab ${activeTab === "regulations" ? "market-intel__tab--active" : ""}`}
              onClick={() => setActiveTab("regulations")}
            >
              {t("marketIntelligence.tabs.regulations", { count: regulationsCount })}
            </button>
            <button
              type="button"
              className={`market-intel__tab ${activeTab === "geopolitics" ? "market-intel__tab--active" : ""}`}
              onClick={() => setActiveTab("geopolitics")}
            >
              {t("marketIntelligence.tabs.geopolitics", { count: geopoliticsCount })}
            </button>
            <button
              type="button"
              className={`market-intel__tab ${activeTab === "business" ? "market-intel__tab--active" : ""}`}
              onClick={() => setActiveTab("business")}
            >
              {t("marketIntelligence.tabs.business", { count: businessNewsCount })}
            </button>
            <button
              type="button"
              className={`market-intel__tab ${activeTab === "watchlist" ? "market-intel__tab--active" : ""}`}
              onClick={() => setActiveTab("watchlist")}
            >
              <i className="fa-solid fa-bookmark" />{" "}
              {t("marketIntelligence.tabs.watchlist", { count: watchlistedCount })}
            </button>
          </div>
        </section>
        {error && (
          <div className="market-intel__error" role="alert">
            <i className="fa-solid fa-circle-exclamation" />
            <span>{error}</span>
          </div>
        )}

        {loading ? (
          <div className="market-intel__loading">
            <i className="fa-solid fa-spinner fa-spin" />
            <span>{t("marketIntelligence.loading")}</span>
          </div>
        ) : (
          <div className="market-intel__list">
            {visibleArticles.length === 0 ? (
              <p className="market-intel__empty">
                {activeTab === "watchlist"
                  ? t("marketIntelligence.empty.watchlist")
                  : t("marketIntelligence.empty.category")}
              </p>
            ) : (
              visibleArticles.map((article) => {
                const togglePending =
                  (addToWatchlist.isPending && addToWatchlist.variables?.id === article.id) ||
                  (removeFromWatchlist.isPending && removeFromWatchlist.variables?.id === article.id);
                return (
                  <article key={article.id} className="market-intel__article-card">
                    <header className="market-intel__article-header">
                      <div className="market-intel__article-badge-wrapper">
                        <span
                          className={`market-intel__article-badge market-intel__article-badge--${categoryBadgeClass(article.category)}`}
                        >
                          {(() => {
                            const key = categoryLabelKey(article.category);
                            return key ? t(key) : categoryLabel(article.category);
                          })()}
                        </span>
                      </div>
                      <h2 className="market-intel__article-title">{article.title}</h2>
                      <ExpandableText
                        className="market-intel__article-summary"
                        text={article.brief ?? ""}
                        maxLines={4}
                      />
                      <div className="market-intel__article-meta-row">
                        <span className="market-intel__article-source">
                          <i className="fa-regular fa-file-lines" />
                          {t("marketIntelligence.article.source", { source: article.source })}
                        </span>
                        <span className="market-intel__article-date">
                          <i className="fa-regular fa-clock" />
                          {formatReleaseDate(article.releaseDate)}
                        </span>
                      </div>
                    </header>

                    <footer className="market-intel__article-footer">
                      <a
                        href={article.fullArticleLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="market-intel__article-button market-intel__article-button--primary"
                      >
                        <i className="fa-regular fa-newspaper" />
                        {t("marketIntelligence.article.readFull")}
                      </a>
                      <button
                        type="button"
                        className="market-intel__article-button market-intel__article-button--secondary"
                        onClick={() => toggleWatchlist(article.id, article.isWatchlisted)}
                        disabled={togglePending}
                        aria-pressed={article.isWatchlisted}
                      >
                        <i className={article.isWatchlisted ? "fa-solid fa-bookmark" : "fa-regular fa-bookmark"} />
                        <span>
                          {article.isWatchlisted
                            ? t("marketIntelligence.article.savedToWatchlist")
                            : t("marketIntelligence.article.addToWatchlist")}
                        </span>
                      </button>
                    </footer>
                  </article>
                );
              })
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default MarketIntelligencePage;
